import { Spin } from "antd";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import type { UserRole } from "../api/types";

// Higher number = more privileged. Mirrors the backend's hierarchy in
// src/middleware/auth.ts (userCanAccessGroup) and user.controller.ts
// (canManageRole): SUPER_ADMIN > FOOD_BANK_ADMIN > GROUP_ADMIN > STAFF.
const ROLE_RANK: Record<UserRole, number> = {
  STAFF: 0,
  GROUP_ADMIN: 1,
  FOOD_BANK_ADMIN: 2,
  SUPER_ADMIN: 3,
};

/**
 * Gate a route (and its children, via nesting) behind login and, optionally,
 * a minimum role. This only controls what the UI shows/hides - the backend
 * re-checks every one of these rules independently per request, so this is
 * about UX, not the actual security boundary.
 */
export function ProtectedRoute({ minRole }: { minRole?: UserRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (minRole && ROLE_RANK[user.role] < ROLE_RANK[minRole]) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
